/**
 * Canonical permission strings for the JWT API (`api` guard).
 *
 * Format: `[group]:[entity]:[action]`
 *   group  : master | data
 *   entity : vattu, pnk, bbgt, ...
 *   action : xem | tao-moi | sua | xoa | duyet | xac-nhan | xuat-excel
 *
 * These strings MUST match exactly what `GET /api/v1/me/abilities` returns.
 * See docs "Permission Format Specification".
 */

// ---------------------------------------------------------------------------
// master group
// ---------------------------------------------------------------------------
export const VAT_TU = {
  XEM: 'master:vattu:xem',
  TAO_MOI: 'master:vattu:tao-moi',
  SUA: 'master:vattu:sua',
  XOA: 'master:vattu:xoa',
  DUYET: 'master:vattu:duyet',
} as const;

export const DANH_MUC_VAT_TU = {
  XEM: 'master:danhmucvattu:xem',
  TAO_MOI: 'master:danhmucvattu:tao-moi',
  SUA: 'master:danhmucvattu:sua',
  XOA: 'master:danhmucvattu:xoa',
} as const;

export const TOA_XE = {
  XEM: 'master:toaxe:xem',
  TAO_MOI: 'master:toaxe:tao-moi',
  SUA: 'master:toaxe:sua',
  XOA: 'master:toaxe:xoa',
  DUYET: 'master:toaxe:duyet',
} as const;

export const DANH_MUC_TOA_XE = {
  XEM: 'master:danhmuctoaxe:xem',
  TAO_MOI: 'master:danhmuctoaxe:tao-moi',
  SUA: 'master:danhmuctoaxe:sua',
  XOA: 'master:danhmuctoaxe:xoa',
} as const;

export const CAP_SUA_CHUA = {
  XEM: 'master:capsc:xem',
  TAO_MOI: 'master:capsc:tao-moi',
  SUA: 'master:capsc:sua',
  XOA: 'master:capsc:xoa',
} as const;

export const DINH_MUC_CO_DINH = {
  XEM: 'master:dinhmuccodinh:xem',
  TAO_MOI: 'master:dinhmuccodinh:tao-moi',
  SUA: 'master:dinhmuccodinh:sua',
  XOA: 'master:dinhmuccodinh:xoa',
} as const;

// ---------------------------------------------------------------------------
// data group
// ---------------------------------------------------------------------------
export const KHO = {
  XEM: 'data:kho:xem',
  TAO_MOI: 'data:kho:tao-moi',
  SUA: 'data:kho:sua',
  XOA: 'data:kho:xoa',
} as const;

export const PHIEU_NHAP_KHO = {
  XEM: 'data:pnk:xem',
  TAO_MOI: 'data:pnk:tao-moi',
  SUA: 'data:pnk:sua',
  XOA: 'data:pnk:xoa',
  DUYET: 'data:pnk:duyet',
} as const;

export const PHIEU_XUAT_KHO = {
  XEM: 'data:pxk:xem',
  TAO_MOI: 'data:pxk:tao-moi',
  SUA: 'data:pxk:sua',
  XOA: 'data:pxk:xoa',
  DUYET: 'data:pxk:duyet',
} as const;

export const VAT_TU_CONG_TY_DUYET = {
  XEM: 'data:vattu-congtyduyet:xem',
  DUYET: 'data:vattu-congtyduyet:duyet',
} as const;

export const BIEN_BAN_GIAI_THE = {
  XEM: 'data:bbgt:xem',
  TAO_MOI: 'data:bbgt:tao-moi',
  SUA: 'data:bbgt:sua',
  XOA: 'data:bbgt:xoa',
  XAC_NHAN: 'data:bbgt:xac-nhan',
  DUYET: 'data:bbgt:duyet',
} as const;

export const HOP_DONG = {
  XEM: 'data:hopdong:xem',
  TAO_MOI: 'data:hopdong:tao-moi',
  SUA: 'data:hopdong:sua',
  XOA: 'data:hopdong:xoa',
} as const;

export const KE_HOACH_SUA_CHUA = {
  XEM: 'data:kehoachsuachua:xem',
  TAO_MOI: 'data:kehoachsuachua:tao-moi',
  SUA: 'data:kehoachsuachua:sua',
  XOA: 'data:kehoachsuachua:xoa',
} as const;

export const NGUOI_DUNG = {
  XEM: 'data:nguoidung:xem',
  TAO_MOI: 'data:nguoidung:tao-moi',
  SUA: 'data:nguoidung:sua',
  XOA: 'data:nguoidung:xoa',
} as const;

// Reports -------------------------------------------------------------------
export const BAO_CAO_SO_CAN_DOI = {
  XEM: 'data:baocao-bcd:xem',
  XUAT_EXCEL: 'data:baocao-bcd:xuat-excel',
} as const;

export const BAO_CAO_CHI_TIET_NHAP = {
  XEM: 'data:baocao-ctn:xem',
  XUAT_EXCEL: 'data:baocao-ctn:xuat-excel',
} as const;

export const BAO_CAO_CHI_TIET_XUAT = {
  XEM: 'data:baocao-ctx:xem',
  XUAT_EXCEL: 'data:baocao-ctx:xuat-excel',
} as const;

export const BAO_CAO_TONG_HOP_NHAP = {
  XEM: 'data:baocao-tonghop-nhap:xem',
  XUAT_EXCEL: 'data:baocao-tonghop-nhap:xuat-excel',
} as const;

export const BAO_CAO_TONG_HOP_XUAT = {
  XEM: 'data:baocao-tonghop-xuat:xem',
  XUAT_EXCEL: 'data:baocao-tonghop-xuat:xuat-excel',
} as const;

export const BAO_CAO_THUC_HIEN_HOP_DONG = {
  XEM: 'data:baocao-thuchienhopdong:xem',
  XUAT_EXCEL: 'data:baocao-thuchienhopdong:xuat-excel',
} as const;

// ---------------------------------------------------------------------------
// Legacy aliases — kept so existing imports keep working, but now point to the
// canonical strings returned by /api/v1/me/abilities.
// ---------------------------------------------------------------------------
export const DUYET_PHIEU_NHAP_PERMISSION = PHIEU_NHAP_KHO.DUYET;
export const DUYET_PHIEU_XUAT_PERMISSION = PHIEU_XUAT_KHO.DUYET;
export const DUYET_VAT_TU_PERMISSION = VAT_TU.DUYET;
export const CONFIRM_BBGT_PERMISSION = BIEN_BAN_GIAI_THE.XAC_NHAN;
export const APPROVE_BBGT_ADMIN_PERMISSION = BIEN_BAN_GIAI_THE.DUYET;
